import moment from 'moment';
import { getApiAxiosInstance } from '../../../../../utils/network';
import { FILTER_VALUES, UrlParams, FILTER_KEYS } from '../../constants';

const { UPCOMING, PAST, IN_PROGRESS } = FILTER_VALUES;
const {
  QUERY,
  SHOW_TENNIS_CONTENT,
  SHOW_PICKLEBALL_CONTENT,
  RANGE,
  SORT_BY,
  SORT,
  TOP,
  SKIP,
  SEARCH_ORIGIN,
} = UrlParams;
const {
  DISTANCE,
  LOCATION_COUNTRY_NAME,
  LOCATION_STATE_NAME,
  PIN,
  TOURS,
  EVENT_TYPES,
  EVENT_TEAM_TYPES,
  GENDERS,
  BALL_TYPES,
  EVENT_UTR_TYPES,
  UTR_MIN,
  UTR_MAX,
  UTR_TYPE,
  UTR_TEAM_TYPE,
  UTR_FIT_POSITION,
  PRIZE_MONEY_MIN,
  PRIZE_MONEY_MAX,
  EVENT_STATE,
} = FILTER_KEYS;

// Simplified API layer for event search
export const eventSearchApi = {
  // Search for events
  async search(searchParams) {
    const axiosInstance = getApiAxiosInstance();
    const params = new URLSearchParams();
    const { query, filters, top, skip, sort, appendType } = searchParams;
    const ascending = 'asc';
    const descending = 'desc';
    const dateFormat = 'MM/DD/YYYY';
    // Add basic parameters
    if (query) {
        params.append(QUERY, query);
    }
    if (top) {
        params.append(TOP, top);
    }
    if (skip) {
        params.append(SKIP, skip);
    }
    params.append(SEARCH_ORIGIN, 'searchPage');

    // Add filter parameters
    if (filters) {
      // Handle sport types - transform to API format
      if (filters.sportTypes && typeof filters.sportTypes === 'object') {
        const { tennis, pickleball } = filters.sportTypes;
        if (tennis && !pickleball) {
          params.append(SHOW_TENNIS_CONTENT, true);
          params.append(SHOW_PICKLEBALL_CONTENT, false);
        } else if (pickleball && !tennis) {
          params.append(SHOW_TENNIS_CONTENT, false);
          params.append(SHOW_PICKLEBALL_CONTENT, true);
        } else if (tennis && pickleball) {
          params.append(SHOW_TENNIS_CONTENT, true);
          params.append(SHOW_PICKLEBALL_CONTENT, true);
        }
      }

      // Location is mutually exclusive: pin+distance OR region names.
      // Sending both AND's them and the V2 events backend returns 0 hits on
      // events whose state field doesn't exactly match the LLM-emitted name
      // (e.g. event stored as "NY" vs filter "New York").
      if (filters.lat && filters.lng && filters.distance) {
        params.append(DISTANCE, filters.distance);
        params.append(PIN, `${filters.lat},${filters.lng}`);
      } else {
        if (filters.locationCountryName) {
          params.append(LOCATION_COUNTRY_NAME, filters.locationCountryName);
        }
        if (filters.locationStateName) {
          params.append(LOCATION_STATE_NAME, filters.locationStateName);
        }
      }

      // Handle date range - convert to range queries
      if (filters.dateRange) {
        const today = moment().format(dateFormat);
        if (filters.dateRange === UPCOMING) {
          params.append(RANGE, `eventSchedule.eventStartUtc>${today}`);
        } else if (filters.dateRange === PAST) {
          params.append(RANGE, `eventSchedule.eventEndUtc<${today}`);
          params.append(SORT, `eventSchedule.eventEndUtc:${descending}`);
        } else if (filters.dateRange === IN_PROGRESS) {
          params.append(RANGE, `eventSchedule.eventStartUtc<=${today}`);
          params.append(RANGE, `eventSchedule.eventEndUtc>=${today}`);
          params.append(SORT, `eventSchedule.eventStartUtc:${descending}`);
        }
      }

      // Handle custom date range - use startDate and endDate if both are present
      if (filters.startDate && filters.endDate) {
        params.append(RANGE, `eventSchedule.eventStartUtc>=${filters.startDate.format(dateFormat)}`);
        params.append(RANGE, `eventSchedule.eventEndUtc<=${filters.endDate.format(dateFormat)}`);
      }

      // Handle filters that need individual parameters (tours, eventTypes)
      const individualParamFilters = [TOURS, EVENT_TYPES, EVENT_TEAM_TYPES, GENDERS, BALL_TYPES, EVENT_UTR_TYPES];
      individualParamFilters.forEach(filterKey => {
        if (filters[filterKey] && typeof filters[filterKey] === 'object') {
          Object.entries(filters[filterKey])
            .filter(([, isActive]) => isActive)
            .forEach(([value]) => {
              params.append(filterKey, value);
            });
        }
      });

      // Handle prize money range - translate indices to API values
      // Index values are 1-based; translate to concrete amounts expected by API
      const prizeMoneyValueByIndex = [0, 500, 1000, 2500, 5000, 10000, 25000];
      const prizeMoneyMaxIndex = prizeMoneyValueByIndex.length;
      const prizeMoneyMinIdx = Number(filters[PRIZE_MONEY_MIN]);
      const prizeMoneyMaxIdx = Number(filters[PRIZE_MONEY_MAX]);
      if (prizeMoneyMinIdx && prizeMoneyMinIdx !== 1) {
        const value = prizeMoneyValueByIndex[prizeMoneyMinIdx - 1];
        if (value !== undefined) {
          params.append('eventPrizeMoneyMin', value);
        }
      }
      if (prizeMoneyMaxIdx && prizeMoneyMaxIdx !== prizeMoneyMaxIndex) {
        const value = prizeMoneyValueByIndex[prizeMoneyMaxIdx - 1];
        if (value !== undefined) {
          params.append('eventPrizeMoneyMax', value);
        }
      }

      // Handle simple value filters
      // Note: EVENT_STATE is mutually exclusive with dateRange and custom date filters
      const simpleFilters = [EVENT_STATE, UTR_MIN, UTR_MAX, UTR_TYPE, UTR_TEAM_TYPE, UTR_FIT_POSITION];
      simpleFilters.forEach(filterKey => {
        const value = filters[filterKey];
        // Skip EVENT_STATE if dateRange or custom date filters are present (mutually exclusive)
        if (filterKey === EVENT_STATE && (filters.dateRange || (filters.startDate && filters.endDate))) {
          return;
        }
        if (value !== null && value !== undefined && value !== '') {
          params.append(filterKey, value);
        }
      });
    }

    // Add sorting
    if (sort?.field) {
      params.append(SORT_BY, sort.field);
      params.append(SORT, sort.asc ? ascending : descending);
    }

    try {
      const endpoint = appendType && filters?.type ? `v2/search/${filters.type}` : 'v2/search';
      const response = await axiosInstance.get(endpoint, { params });

      return {
        hits: response.data.hits || [],
        total: response.data.total || 0,
        totalAllowed: response.data.totalAllowed || response.data.total || 0,
        aggregations: response.data.aggregations || {},
      };
    } catch (error) {
      console.error('Event search API error:', error);
      throw new Error(error.response?.data?.message || 'Search failed');
    }
  },

  // Get tour aggregations for event filters
  async getTourAggregations() {
    const axiosInstance = getApiAxiosInstance();
    const eventTourStatusId = 'eventTours.tourStatusId:!2';
    const eventTourName = 'eventTours.name';

    try {
      const aggregations = {
          filter: [eventTourStatusId],
          terms: [{
              term: eventTourName,
          }],
        };

      const response = await axiosInstance.post('v2/search/events/aggregations', aggregations);

      return response.data[eventTourName] || [];
    } catch (error) {
      console.error('Tour aggregations error:', error);
      return [];
    }
  },
};
